import React from "react";
import { Box, Button } from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";

function AttachmentDropZone({
                                file,
                                onFileSelect,
                                accept = "image/*,.pdf,.doc,.docx",
                                maxSizeMB = 2,
                                disabled = false,
                                id = "formFile",
                                rulesText, // e.g. "Max 10 MB • PDF/JPG/PNG"
                                downloadUrl, // NEW: if provided, show Download button
                                downloadName, // NEW: optional filename for download
                                downloadLabel = "Download", // NEW: label text
                            }) {
    const [dragOver, setDragOver] = React.useState(false);
    const [previewUrl, setPreviewUrl] = React.useState("");
    const [error, setError] = React.useState("");
    const inputRef = React.useRef(null);
    const maxBytes = maxSizeMB * 1024 * 1024;

    React.useEffect(() => {
        if (file && file.type?.startsWith?.("image/")) {
            const url = URL.createObjectURL(file);
            setPreviewUrl(url);
            return () => URL.revokeObjectURL(url);
        }
        setPreviewUrl("");
    }, [file]);

    const openPicker = () => !disabled && inputRef.current?.click();

    const validate = (f) => {
        if (!f) return "No file selected.";
        if (f.size > maxBytes) return `Max ${maxSizeMB} MB.`;
        const allowed = accept.split(",").map((s) => s.trim().toLowerCase());
        const ext = "." + (f.name.split(".").pop() || "").toLowerCase();
        const isMatch =
            allowed.includes("*/*") ||
            allowed.includes(f.type.toLowerCase()) ||
            allowed.some(
                (a) =>
                    a.endsWith("/*") &&
                    f.type.toLowerCase().startsWith(a.replace("/*", "/"))
            ) ||
            allowed.includes(ext);
        return isMatch ? "" : "Unsupported file type.";
    };

    const setFile = (f) => {
        if (disabled) return;
        const err = f ? validate(f) : "";
        setError(err);
        onFileSelect(err ? null : f);
    };

    const onInputChange = (e) => setFile(e.target.files?.[0] || null);
    const onDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
        !disabled && setDragOver(true);
    };
    const onDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragOver(false);
    };
    const onDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragOver(false);
        if (disabled) return;
        const f = e.dataTransfer.files?.[0];
        setFile(f);
    };
    const clearFile = (e) => {
        e.stopPropagation();
        setError("");
        onFileSelect(null);
    };

    const isImage = !!previewUrl;
    const hasFile = !!file;
    const rules = rulesText || `Max ${maxSizeMB} MB • ${accept}`;
    const showDownload = !!downloadUrl;

    function middleTruncate(str, max = 56) {
        if (!str || str.length <= max) return str;
        const dot = str.lastIndexOf(".");
        const ext = dot > 0 ? str.slice(dot) : "";
        const core = dot > 0 ? str.slice(0, dot) : str;
        const keep = Math.max(3, max - ext.length - 3); // reserve for "..."
        const head = Math.ceil(keep * 0.6);
        const tail = keep - head;
        return `${core.slice(0, head)}...${core.slice(core.length - tail)}${ext}`;
    }

    return (
        <Box aria-live="polite">
            <Box
                role="button"
                tabIndex={0}
                aria-label="Upload attachment"
                onClick={openPicker}
                onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && openPicker()}
                onDragOver={onDragOver}
                onDragLeave={onDragLeave}
                onDrop={onDrop}
                sx={(theme) => ({
                    border: "2px dashed",
                    borderRadius: 1.5,
                    p: 3,
                    textAlign: "center",
                    cursor: disabled ? "not-allowed" : "pointer",
                    transition: "border-color 120ms ease, background 120ms ease",
                    minHeight: 140,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    position: "relative",
                    borderColor: error
                        ? theme.palette.error.main
                        : dragOver
                            ? theme.palette.primary.main
                            : theme.palette.mode === "dark"
                                ? theme.palette.grey[700]
                                : theme.palette.grey[400],
                    background: error
                        ? theme.palette.mode === "dark"
                            ? "rgba(244, 67, 54, 0.08)"
                            : "rgba(244, 67, 54, 0.06)"
                        : dragOver
                            ? theme.palette.mode === "dark"
                                ? "rgba(144,202,249,0.08)"
                                : "rgba(25,118,210,0.06)"
                            : hasFile
                                ? theme.palette.mode === "dark"
                                    ? "rgba(76,175,80,0.06)"
                                    : "rgba(76,175,80,0.05)"
                                : "transparent",
                })}
            >
                {!hasFile && !error && (
                    <Box>
                        <CloudUploadIcon fontSize="large" />
                        <Box sx={{ mt: 1, fontWeight: 600 }}>Drag & drop your file here</Box>
                        <Box sx={{ color: "text.secondary", fontSize: 14, mt: 0.5 }}>
                            or <span style={{ textDecoration: "underline" }}>click to browse</span>
                        </Box>
                        <Box sx={{ color: "text.secondary", fontSize: 12, mt: 1 }}>
                            {rules}
                        </Box>
                    </Box>
                )}

                {hasFile && !error && (
                    <Box
                        sx={{
                            width: "100%",
                            display: "flex",
                            gap: 2,
                            alignItems: "center",
                            justifyContent: "flex-start",
                        }}
                    >
                        <CheckCircleRoundedIcon color="success" sx={{ fontSize: 28 }} />

                        {isImage ? (
                            <Box
                                component="img"
                                src={previewUrl}
                                alt={file.name}
                                sx={{
                                    width: 72,
                                    height: 72,
                                    objectFit: "cover",
                                    borderRadius: 1,
                                    border: "1px solid",
                                    borderColor: "divider",
                                    flexShrink: 0,
                                }}
                            />
                        ) : (
                            <Box
                                sx={{
                                    width: 72,
                                    height: 72,
                                    borderRadius: 1,
                                    border: "1px solid",
                                    borderColor: "divider",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    flexShrink: 0,
                                    fontSize: 12,
                                }}
                            >
                                {"File"}
                            </Box>
                        )}

                        <Box sx={{ minWidth: 0, textAlign: "left", flex: 1 }}>
                            <Box
                                sx={{
                                    fontSize: 14,
                                    fontWeight: 600,
                                    display: "-webkit-box",
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: "vertical",
                                    overflow: "hidden",
                                    wordBreak: "break-word",
                                }}
                                title={file.name}
                            >
                                {middleTruncate(file.name, 64)}
                            </Box>
                        </Box>

                        {/* Right actions: Download (if URL) above Clear */}
                        <Box sx={{ ml: "auto", display: "flex", flexDirection: "column", gap: 1 }}>
                            {showDownload && (
                                <Button
                                    size="medium"
                                    variant="outlined"
                                    color="primary"
                                    startIcon={<DownloadOutlinedIcon />}
                                    component="a"
                                    href={downloadUrl}
                                    download={downloadName || undefined}
                                    target="_blank"
                                    rel="noreferrer"
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    {downloadLabel}
                                </Button>
                            )}

                            <Button
                                size="medium"
                                variant="outlined"
                                color="inherit"
                                startIcon={<DeleteOutlineIcon />}
                                onClick={clearFile}
                            >
                                Clear
                            </Button>
                        </Box>
                    </Box>
                )}

                {error && (
                    <Box sx={{ width: "100%" }}>
                        <Box
                            sx={{
                                width: "100%",
                                display: "flex",
                                gap: 1,
                                alignItems: "center",
                                justifyContent: "center",
                                mb: 1,
                            }}
                        >
                            <ErrorOutlineIcon color="error" />
                            <Box sx={{ color: "error.main", fontSize: 14 }}>{error}</Box>
                        </Box>

                        {/* Right actions: Download (if URL) above Clear, aligned right */}
                        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                                {showDownload && (
                                    <Button
                                        size="medium"
                                        variant="outlined"
                                        color="primary"
                                        startIcon={<DownloadOutlinedIcon />}
                                        component="a"
                                        href={downloadUrl}
                                        download={downloadName || undefined}
                                        target="_blank"
                                        rel="noreferrer"
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        {downloadLabel}
                                    </Button>
                                )}

                                <Button
                                    size="medium"
                                    variant="outlined"
                                    color="inherit"
                                    startIcon={<DeleteOutlineIcon />}
                                    onClick={clearFile}
                                >
                                    Clear
                                </Button>
                            </Box>
                        </Box>
                    </Box>
                )}

                <input
                    ref={inputRef}
                    id={id}
                    type="file"
                    accept={accept}
                    onChange={onInputChange}
                    style={{ display: "none" }}
                    disabled={disabled}
                />
            </Box>
        </Box>
    );
}

export default AttachmentDropZone;
